import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import {
  BarChart3, DollarSign, Users, TrendingUp, Download, Loader2, AlertTriangle,
  FileSpreadsheet, Map as MapIcon, PieChart, Layers, ChevronRight, Wrench,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import GeographyPage from './GeographyPage';

const ALL = '__all__';
const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());

function MetricCard({ label, value, sub, testid, onClick }) {
  return (
    <Card
      className={`p-4 border-slate-200 ${onClick ? 'cursor-pointer hover:border-sky-400 hover:shadow-md transition-all' : ''}`}
      onClick={onClick} data-testid={testid}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

function FilterSelect({ label, value, onChange, options, testid, optionLabel = (o) => o, optionValue = (o) => o }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? '' : v)}>
        <SelectTrigger className="h-9" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map((o, i) => (
            <SelectItem key={i} value={String(optionValue(o))}>{optionLabel(o)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FilterPanel({ options, filters, patch, onRun, loading, extra = true }) {
  if (!options) return null;
  return (
    <Card className="p-4 border-slate-200 bg-slate-50/60">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <FilterSelect label="Workspace" value={filters.workspace_id} onChange={(v) => patch({ workspace_id: v })}
          options={options.workspaces} optionLabel={(o) => o.name} optionValue={(o) => o.id} testid="filter-workspace" />
        <FilterSelect label="Coverage Year" value={filters.coverage_year_id} onChange={(v) => patch({ coverage_year_id: v })}
          options={options.coverage_years} optionLabel={(o) => o.label} optionValue={(o) => o.id} testid="filter-coverage-year" />
        <FilterSelect label="Program Type" value={filters.program_type} onChange={(v) => patch({ program_type: v })}
          options={options.programs} testid="filter-program" />
        <FilterSelect label="Enrollment Type" value={filters.intake_type} onChange={(v) => patch({ intake_type: v })}
          options={options.intake_types} testid="filter-intake" />
        {extra && <>
          <FilterSelect label="Office" value={filters.office} onChange={(v) => patch({ office: v })}
            options={options.offices} testid="filter-office" />
          <FilterSelect label="Carrier" value={filters.carrier} onChange={(v) => patch({ carrier: v })}
            options={options.carriers} testid="filter-carrier" />
          <FilterSelect label="Product Type" value={filters.product_type} onChange={(v) => patch({ product_type: v })}
            options={options.product_types} testid="filter-product" />
          <FilterSelect label="Market Type" value={filters.market_type} onChange={(v) => patch({ market_type: v })}
            options={options.market_types} testid="filter-market" />
          <FilterSelect label="Policy Status" value={filters.policy_status} onChange={(v) => patch({ policy_status: v })}
            options={options.policy_statuses} testid="filter-policy-status" />
          <FilterSelect label="Premium Support" value={filters.premium_support_status} onChange={(v) => patch({ premium_support_status: v })}
            options={options.premium_support_statuses} testid="filter-premium-support" />
          {options.agents?.length > 0 && (
            <FilterSelect label="Agent / Producer" value={filters.agent_id} onChange={(v) => patch({ agent_id: v })}
              options={options.agents} optionLabel={(o) => o.name} optionValue={(o) => o.id} testid="filter-agent" />
          )}
          <FilterSelect label="Case Manager" value={filters.case_manager_id} onChange={(v) => patch({ case_manager_id: v })}
            options={options.case_managers} optionLabel={(o) => o.name} optionValue={(o) => o.id} testid="filter-case-manager" />
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Premium range ($/mo)</Label>
            <div className="flex gap-1">
              <Input type="number" className="h-9" placeholder="min" value={filters.premium_min ?? ''}
                onChange={(e) => patch({ premium_min: e.target.value === '' ? null : Number(e.target.value) })} data-testid="filter-premium-min" />
              <Input type="number" className="h-9" placeholder="max" value={filters.premium_max ?? ''}
                onChange={(e) => patch({ premium_max: e.target.value === '' ? null : Number(e.target.value) })} data-testid="filter-premium-max" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">FPL range (%)</Label>
            <div className="flex gap-1">
              <Input type="number" className="h-9" placeholder="min" value={filters.fpl_min ?? ''}
                onChange={(e) => patch({ fpl_min: e.target.value === '' ? null : Number(e.target.value) })} data-testid="filter-fpl-min" />
              <Input type="number" className="h-9" placeholder="max" value={filters.fpl_max ?? ''}
                onChange={(e) => patch({ fpl_max: e.target.value === '' ? null : Number(e.target.value) })} data-testid="filter-fpl-max" />
            </div>
          </div>
        </>}
      </div>
      <div className="mt-4">
        <Button onClick={onRun} disabled={loading} className="bg-sky-600 hover:bg-sky-700" data-testid="run-report-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
          Run Report
        </Button>
      </div>
    </Card>
  );
}

function ReportHeader({ header }) {
  if (!header) return null;
  const chips = Object.entries(header.applied_filters || {}).filter(([k]) => !['workspace_id', 'coverage_year_id'].includes(k));
  return (
    <Card className="p-4 border-slate-200" data-testid="report-header">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-slate-900">{header.report_name}</h3>
          <p className="text-sm text-slate-500">
            {header.workspace} · {header.coverage_year} · {header.program} · {header.unique_clients} unique clients
          </p>
        </div>
        <span className="text-xs text-slate-400">Generated {new Date(header.generated_at).toLocaleString()}</span>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {chips.map(([k, v]) => <Badge key={k} variant="secondary" className="text-xs">{k.replace(/_/g, ' ')}: {String(v)}</Badge>)}
        </div>
      )}
    </Card>
  );
}

function DrillDialog({ open, onClose, title, loading, data }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-auto" data-testid="drilldown-dialog">
        <DialogHeader><DialogTitle>{title} {data ? `· ${data.count} cases` : ''}</DialogTitle></DialogHeader>
        {loading ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-sky-500" /></div> : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2 pr-3">Client ID</th><th className="pr-3">Name</th><th className="pr-3">Program</th>
                  <th className="pr-3">Carrier</th><th className="pr-3">Premium</th><th className="pr-3">Org Paid</th>
                  <th className="pr-3">FPL %</th><th className="pr-3">Agent</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((r) => (
                  <tr key={r.case_id} className="border-b border-slate-100" data-testid="drill-row">
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.calyvra_client_id || '—'}</td>
                    <td className="pr-3">{r.client_name || '—'}</td>
                    <td className="pr-3">{r.program_type || '—'}</td>
                    <td className="pr-3">{r.carrier || '—'}</td>
                    <td className="pr-3">{money(r.monthly_premium)}</td>
                    <td className="pr-3">{money(r.org_paid)}</td>
                    <td className="pr-3">{r.fpl_status === 'ok' ? `${r.fpl_pct}%` : '—'}</td>
                    <td className="pr-3">{r.agent || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data && data.count > (data.items?.length || 0) && (
              <p className="text-xs text-slate-400 mt-2">Showing first {data.items.length} of {data.count}.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ReportsPage() {
  const { activeWorkspaceId, isCalyvraAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState({ workspace_id: activeWorkspaceId || '' });
  const [loading, setLoading] = useState(false);
  const [pa, setPa] = useState(null);
  const [er, setEr] = useState(null);
  const [drill, setDrill] = useState({ open: false, title: '', loading: false, data: null });

  const patch = (p) => setFilters((f) => ({ ...f, ...p }));
  const setTab = (t) => setSearchParams((sp) => { sp.set('tab', t); return sp; }, { replace: true });

  const loadOptions = useCallback(async (wsId) => {
    try {
      const o = await portalApi.getReportFilterOptions(wsId);
      setOptions(o);
    } catch (e) { toast.error(e.message); }
  }, []);

  useEffect(() => { loadOptions(filters.workspace_id); /* eslint-disable-next-line */ }, [filters.workspace_id]);

  const cleanFilters = () => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) out[k] = v; });
    return out;
  };

  const runPremium = async () => {
    setLoading(true);
    try { setPa(await portalApi.reportPremiumAssistance(cleanFilters())); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  const runEnrollment = async () => {
    setLoading(true);
    try { setEr(await portalApi.reportEnrollmentRenewals(cleanFilters())); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const openDrill = async (title, body) => {
    setDrill({ open: true, title, loading: true, data: null });
    try {
      const data = await portalApi.reportDrillDown({ filters: cleanFilters(), ...body });
      setDrill({ open: true, title, loading: false, data });
    } catch (e) { toast.error(e.message); setDrill({ open: false }); }
  };

  const exportReport = async (report, type) => {
    try { await portalApi.exportReport({ filters: cleanFilters(), report, type }); toast.success('Export started'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-sky-500" /> Reports
        </h1>
        <p className="text-slate-500 text-sm">Operational, program & Premium Assistance reporting from live CRM data.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto" data-testid="reports-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview"><Layers className="h-4 w-4 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="premium" data-testid="tab-premium"><DollarSign className="h-4 w-4 mr-1" /> Premium Assistance</TabsTrigger>
          <TabsTrigger value="enrollment" data-testid="tab-enrollment"><TrendingUp className="h-4 w-4 mr-1" /> Enrollment & Renewals</TabsTrigger>
          <TabsTrigger value="program" data-testid="tab-program"><PieChart className="h-4 w-4 mr-1" /> Program Metrics</TabsTrigger>
          <TabsTrigger value="builder" data-testid="tab-builder"><FileSpreadsheet className="h-4 w-4 mr-1" /> Report Builder</TabsTrigger>
          <TabsTrigger value="geography" data-testid="tab-geography"><MapIcon className="h-4 w-4 mr-1" /> Geography</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <Card className="p-6 border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-2">Reporting engine</h3>
            <p className="text-sm text-slate-600 max-w-3xl">
              Define a population using the global filters, then calculate metrics. The same population powers
              Premium Assistance, Enrollment & Renewals, Program Metrics, the Report Builder and exports — one
              source of truth. Missing information is never silently treated as zero; see each report's
              Data Quality section.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              {[['premium', 'Premium Assistance', 'FPL, premium thresholds, org-paid'],
                ['enrollment', 'Enrollment & Renewals', 'New vs Renewal, by agent/carrier'],
                ['builder', 'Report Builder', 'COUNT / SUM / AVG grouped']].map(([t, title, sub]) => (
                <Card key={t} className="p-4 cursor-pointer hover:border-sky-400 transition-all" onClick={() => setTab(t)} data-testid={`overview-card-${t}`}>
                  <div className="font-medium text-slate-900 flex items-center justify-between">{title} <ChevronRight className="h-4 w-4 text-sky-500" /></div>
                  <div className="text-xs text-slate-500 mt-1">{sub}</div>
                </Card>
              ))}
            </div>
          </Card>

          <DataTools show={isCalyvraAdmin} />
        </TabsContent>

        {/* Premium Assistance */}
        <TabsContent value="premium" className="space-y-4 mt-4">
          <FilterPanel options={options} filters={filters} patch={patch} onRun={runPremium} loading={loading} />
          {pa && (
            <>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => exportReport('premium_assistance', 'aggregate')} data-testid="export-pa-aggregate">
                  <Download className="h-4 w-4 mr-1" /> Aggregate CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportReport('premium_assistance', 'detail')} data-testid="export-pa-detail">
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Detail CSV
                </Button>
              </div>
              <ReportHeader header={pa.header} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard label="Unique Clients" value={num(pa.metrics.clients)} testid="pa-clients" />
                <MetricCard label="Total Monthly Premium" value={money(pa.metrics.total_monthly_premium)} testid="pa-total-premium" />
                <MetricCard label="Avg Monthly Premium" value={money(pa.metrics.avg_monthly_premium)} testid="pa-avg-premium" />
                <MetricCard label="Total Org-Paid" value={money(pa.metrics.total_org_paid)} sub="premium_assistance_amount" testid="pa-total-orgpaid" />
                <MetricCard label="Avg Org-Paid" value={money(pa.metrics.avg_org_paid)} testid="pa-avg-orgpaid" />
                <MetricCard label="Total Member Resp." value={money(pa.metrics.total_member_resp)} testid="pa-total-memberresp" />
                <MetricCard label="Premium ≤ $225" value={num(pa.metrics.premium_le225)}
                  onClick={() => openDrill('Premium ≤ $225', { premium_group: 'le225' })} testid="pa-prem-le225" />
                <MetricCard label="Premium > $225" value={num(pa.metrics.premium_gt225)}
                  onClick={() => openDrill('Premium > $225', { premium_group: 'gt225' })} testid="pa-prem-gt225" />
              </div>

              {/* FPL distribution */}
              <Card className="p-4 border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">FPL Distribution</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[['Below 100%', 'below_100', 'below100'], ['100–250%', '100_to_250', '100_250'],
                    ['>250–300%', 'above_250_to_300', '250_300'], ['Above 300%', 'above_300', 'above300']].map(([lbl, key, grp]) => (
                    <MetricCard key={key} label={lbl} value={num(pa.fpl_distribution[key].count)}
                      sub={`${pa.fpl_distribution[key].pct}%`} onClick={() => openDrill(`FPL ${lbl}`, { fpl_group: grp })}
                      testid={`pa-fpl-${grp}`} />
                  ))}
                  <MetricCard label="Unable to Calculate" value={num(pa.fpl_distribution.unable_to_calculate)}
                    onClick={() => openDrill('FPL Unable to Calculate', { missing: 'fpl' })} testid="pa-fpl-unable" />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Calculated: {pa.fpl_distribution.calculated_total} · reconciles to population minus "Unable to Calculate".
                </p>
              </Card>

              {/* FPL x Premium matrix */}
              <Card className="p-4 border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">FPL × Premium Threshold (non-overlapping)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="≤300% FPL & ≤$225" value={num(pa.fpl_premium_matrix.le300_le225)}
                    onClick={() => openDrill('≤300% FPL & ≤$225', { fpl_group: 'le300', premium_group: 'le225' })} testid="pa-mx-1" />
                  <MetricCard label="≤300% FPL & >$225" value={num(pa.fpl_premium_matrix.le300_gt225)}
                    onClick={() => openDrill('≤300% FPL & >$225', { fpl_group: 'le300', premium_group: 'gt225' })} testid="pa-mx-2" />
                  <MetricCard label=">300% FPL & ≤$225" value={num(pa.fpl_premium_matrix.gt300_le225)}
                    onClick={() => openDrill('>300% FPL & ≤$225', { fpl_group: 'gt300', premium_group: 'le225' })} testid="pa-mx-3" />
                  <MetricCard label=">300% FPL & >$225" value={num(pa.fpl_premium_matrix.gt300_gt225)}
                    onClick={() => openDrill('>300% FPL & >$225', { fpl_group: 'gt300', premium_group: 'gt225' })} testid="pa-mx-4" />
                </div>
                <p className="text-xs text-slate-400 mt-2" data-testid="pa-mx-reconcile">
                  Reconciliation: {pa.fpl_premium_matrix.reconciliation} of {pa.fpl_premium_matrix.eligible_total} eligible (FPL calculated & premium available).
                </p>
              </Card>

              {/* Program breakdown */}
              {Object.keys(pa.program_breakdown).length > 0 && (
                <Card className="p-4 border-slate-200 overflow-auto">
                  <h3 className="font-semibold text-slate-900 mb-3">Program Breakdown</h3>
                  <ProgramTable breakdown={pa.program_breakdown} total={pa.metrics} totalMatrix={pa.fpl_premium_matrix}
                    onDrill={(prog) => openDrill(`Program: ${prog}`, { program_type: prog })} />
                </Card>
              )}

              {/* Data quality */}
              <Card className="p-4 border-amber-200 bg-amber-50/40">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> Data Quality
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="Report Population" value={num(pa.data_quality.population)} testid="dq-population" />
                  <MetricCard label="FPL Calculated" value={num(pa.data_quality.fpl_calculated)} testid="dq-fpl-calc" />
                  <MetricCard label="FPL Unable" value={num(pa.data_quality.fpl_unable)}
                    onClick={() => openDrill('FPL Unable to Calculate', { missing: 'fpl' })} testid="dq-fpl-unable" />
                  <MetricCard label="Premium Missing" value={num(pa.data_quality.premium_missing)}
                    onClick={() => openDrill('Premium Missing', { missing: 'premium' })} testid="dq-premium-missing" />
                  <MetricCard label="Income Missing" value={num(pa.data_quality.income_missing)}
                    onClick={() => openDrill('Income Missing', { missing: 'income' })} testid="dq-income-missing" />
                  <MetricCard label="Household Missing" value={num(pa.data_quality.household_missing)}
                    onClick={() => openDrill('Household Size Missing', { missing: 'household' })} testid="dq-household-missing" />
                  <MetricCard label="Org-Paid Missing" value={num(pa.data_quality.org_paid_missing)} testid="dq-orgpaid-missing" />
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Enrollment & Renewals */}
        <TabsContent value="enrollment" className="space-y-4 mt-4">
          <FilterPanel options={options} filters={filters} patch={patch} onRun={runEnrollment} loading={loading} />
          {er && (
            <>
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => exportReport('enrollment_renewals', 'aggregate')} data-testid="export-er-aggregate">
                  <Download className="h-4 w-4 mr-1" /> Aggregate CSV
                </Button>
              </div>
              <ReportHeader header={er.header} />
              <div className="grid grid-cols-3 gap-3">
                <MetricCard label="Total Cases" value={num(er.total_cases)} testid="er-total" />
                <MetricCard label="New" value={num(er.new)} testid="er-new" />
                <MetricCard label="Renewal" value={num(er.renewal)} testid="er-renewal" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {er.by_agent && <BreakdownTable title="By Agent / Producer" data={er.by_agent} testid="er-by-agent" />}
                <BreakdownTable title="By Carrier" data={er.by_carrier} testid="er-by-carrier" />
                <BreakdownTable title="By Program" data={er.by_program} testid="er-by-program" />
                <BreakdownTable title="By Office" data={er.by_office} testid="er-by-office" />
              </div>
            </>
          )}
        </TabsContent>

        {/* Program Metrics */}
        <TabsContent value="program" className="space-y-4 mt-4">
          <FilterPanel options={options} filters={filters} patch={patch} onRun={runPremium} loading={loading} />
          {pa && Object.keys(pa.program_breakdown).length > 0 ? (
            <Card className="p-4 border-slate-200 overflow-auto">
              <ReportHeader header={pa.header} />
              <h3 className="font-semibold text-slate-900 my-3">Program Comparison</h3>
              <ProgramTable breakdown={pa.program_breakdown} total={pa.metrics} totalMatrix={pa.fpl_premium_matrix}
                onDrill={(prog) => openDrill(`Program: ${prog}`, { program_type: prog })} />
            </Card>
          ) : pa ? <p className="text-sm text-slate-500">No program data for this population.</p> : (
            <p className="text-sm text-slate-500">Run the report to compare programs.</p>
          )}
        </TabsContent>

        {/* Report Builder */}
        <TabsContent value="builder" className="space-y-4 mt-4">
          <ReportBuilder options={options} baseFilters={filters} patch={patch} isCalyvra={isCalyvraAdmin} />
        </TabsContent>

        {/* Geography */}
        <TabsContent value="geography" className="mt-4">
          <GeographyPage embedded />
        </TabsContent>
      </Tabs>

      <DrillDialog open={drill.open} onClose={() => setDrill({ open: false })}
        title={drill.title} loading={drill.loading} data={drill.data} />
    </div>
  );
}

function DataTools({ show }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  if (!show) return null;

  const mergeHiv = async () => {
    if (!window.confirm('Merge all cases with program "HIV" into "HIV Care"? This consolidates the report and cannot be undone automatically.')) return;
    setRunning(true);
    try {
      const r = await portalApi.normalizeProgramType('HIV', 'HIV Care');
      setResult(r);
      toast.success(`Merged ${r.updated} case(s) from "HIV" into "HIV Care".`);
    } catch (e) { toast.error(e.message); } finally { setRunning(false); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="data-tools">
      <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Wrench className="h-4 w-4 text-sky-500" /> Data Tools (Admin)
      </h3>
      <p className="text-sm text-slate-500 mb-4">One-time data clean-ups. Idempotent — safe to re-run.</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="font-medium text-slate-800 text-sm">Merge program "HIV" → "HIV Care"</div>
          <div className="text-xs text-slate-500">Legacy imports used "HIV"; the catalog only has PrEP & HIV Care. This consolidates them.</div>
        </div>
        <Button onClick={mergeHiv} disabled={running} className="bg-sky-600 hover:bg-sky-700" data-testid="merge-hiv-btn">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wrench className="h-4 w-4 mr-2" />}
          Merge "HIV" into "HIV Care"
        </Button>
      </div>
      {result && (
        <div className="mt-3 text-sm text-slate-600" data-testid="merge-hiv-result">
          Matched {result.matched} · Updated {result.updated}. {result.updated === 0 ? 'Nothing left to merge.' : 'Re-run any report to see the consolidated totals.'}
        </div>
      )}
    </Card>
  );
}

function ProgramTable({ breakdown, total, totalMatrix, onDrill }) {
  const progs = Object.keys(breakdown);
  const rows = [
    ['Clients Receiving Assistance', (m) => num(m.clients)],
    ['Total Monthly Premium', (m) => money(m.total_monthly_premium)],
    ['Avg Monthly Premium', (m) => money(m.avg_monthly_premium)],
    ['Total Org-Paid', (m) => money(m.total_org_paid)],
    ['Avg Org-Paid', (m) => money(m.avg_org_paid)],
    ['Premium ≤ $225', (m) => num(m.premium_le225)],
    ['Premium > $225', (m) => num(m.premium_gt225)],
  ];
  return (
    <table className="w-full text-sm" data-testid="program-table">
      <thead>
        <tr className="text-left text-xs text-slate-500 border-b">
          <th className="py-2 pr-3">Metric</th>
          {progs.map((p) => (
            <th key={p} className="pr-3 cursor-pointer hover:text-sky-600" onClick={() => onDrill(p)} data-testid={`program-col-${p}`}>{p}</th>
          ))}
          <th className="pr-3">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([lbl, fn]) => (
          <tr key={lbl} className="border-b border-slate-100">
            <td className="py-1.5 pr-3 text-slate-600">{lbl}</td>
            {progs.map((p) => <td key={p} className="pr-3">{fn(breakdown[p])}</td>)}
            <td className="pr-3 font-medium">{fn(total)}</td>
          </tr>
        ))}
        <tr className="border-b border-slate-100">
          <td className="py-1.5 pr-3 text-slate-600">≤300% FPL</td>
          {progs.map((p) => <td key={p} className="pr-3">{num(breakdown[p].fpl_premium_matrix.fpl_le300)}</td>)}
          <td className="pr-3 font-medium">{num(totalMatrix.fpl_le300)}</td>
        </tr>
        <tr>
          <td className="py-1.5 pr-3 text-slate-600">&gt;300% FPL</td>
          {progs.map((p) => <td key={p} className="pr-3">{num(breakdown[p].fpl_premium_matrix.fpl_gt300)}</td>)}
          <td className="pr-3 font-medium">{num(totalMatrix.fpl_gt300)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function BreakdownTable({ title, data, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1].total - a[1].total);
  return (
    <Card className="p-4 border-slate-200" data-testid={testid}>
      <h4 className="font-medium text-slate-900 mb-2">{title}</h4>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b">
            <th className="py-1.5 pr-3">Group</th><th className="pr-3">Total</th><th className="pr-3">New</th><th className="pr-3">Renewal</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-b border-slate-100">
              <td className="py-1.5 pr-3">{k}</td><td className="pr-3 font-medium">{v.total}</td>
              <td className="pr-3">{v.new}</td><td className="pr-3">{v.renewal}</td>
            </tr>
          ))}
          {entries.length === 0 && <tr><td colSpan={4} className="py-3 text-slate-400 text-sm">No data</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function ReportBuilder({ options, baseFilters, patch, isCalyvra }) {
  const [metric, setMetric] = useState('count');
  const [field, setField] = useState('monthly_premium');
  const [groupBy, setGroupBy] = useState('carrier');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const groupOptions = ['program_type', 'agent', 'carrier', 'office', 'coverage_year', 'intake_type', 'state', 'county', 'case_manager']
    .filter((g) => isCalyvra || g !== 'agent');

  const run = async () => {
    setLoading(true);
    try {
      const clean = {};
      Object.entries(baseFilters).forEach(([k, v]) => { if (v !== '' && v != null) clean[k] = v; });
      const body = { filters: clean, metric, group_by: groupBy || null };
      if (metric !== 'count') body.field = field;
      setResult(await portalApi.reportBuilder(body));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  return (
    <>
      <FilterPanel options={options} filters={baseFilters} patch={patch} onRun={run} loading={loading} />
      <Card className="p-4 border-slate-200">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Metric</Label>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="h-9" data-testid="builder-metric"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="count">COUNT</SelectItem>
                <SelectItem value="sum">SUM</SelectItem>
                <SelectItem value="avg">AVERAGE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {metric !== 'count' && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Field</Label>
              <Select value={field} onValueChange={setField}>
                <SelectTrigger className="h-9" data-testid="builder-field"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly_premium">Monthly Premium</SelectItem>
                  <SelectItem value="org_paid">Org-Paid</SelectItem>
                  <SelectItem value="member_resp">Member Responsibility</SelectItem>
                  <SelectItem value="aptc">APTC</SelectItem>
                  <SelectItem value="annual_income">Annual Income</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Group By</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="h-9" data-testid="builder-groupby"><SelectValue /></SelectTrigger>
              <SelectContent>
                {groupOptions.map((g) => (
                  <SelectItem key={g} value={g}>{g.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>
      {result && (
        <Card className="p-4 border-slate-200 overflow-auto">
          <ReportHeader header={result.header} />
          <table className="w-full text-sm mt-3" data-testid="builder-result">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2 pr-3">{(result.group_by || '').replace(/_/g, ' ') || 'Group'}</th>
                <th className="pr-3">{result.metric.toUpperCase()}{result.field ? ` (${result.field})` : ''}</th>
                <th className="pr-3">Rows</th>
              </tr>
            </thead>
            <tbody>
              {(result.data || []).map((d, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid="builder-row">
                  <td className="py-1.5 pr-3">{d.group}</td>
                  <td className="pr-3 font-medium">{result.metric === 'count' ? num(d.value) : (result.field === 'annual_income' || ['monthly_premium', 'org_paid', 'member_resp', 'aptc'].includes(result.field) ? money(d.value) : num(d.value))}</td>
                  <td className="pr-3 text-slate-400">{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
